/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Snigdha Dhanireddy <snigdha.dhanireddy@mroads.com>, May 2023
 * @flow
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import useStyles from './useStyles';
import { Typography, Button, IconButton, Box } from '@material-ui/core';
import { HighlightOff as HighlightOffIcon } from '@material-ui/icons';
import { CountryPicker, getPickerData } from "../../../../components/CountryPicker";
import FireboardUtils from '../../../../util/fireboard-util';
import TextInput from '../TextInput';

const EmailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const defaultInputFields = {
    nameEnabled: true,
    nameRequired: true,
    phoneNumberEnabled: true,
    phoneNumberRequired: true,
    emailAddressEnabled: true,
    emailAddressRequired: true,
};

const CreateWaitList = ({ goToWaitlistScreen, onCreateBooking }) => {
    const classes = useStyles();
    const [selectedCountry, setSelectedCountry] = useState({});
    const [state, setState] = useState({
        phoneNumber: '',
        name: '',
        emailAddress: '',
        partySize: '2',
        note: '',
        staffComment: '',
    });

    const { labelsData } = useSelector(state => state);
    const { labels: strings } = labelsData;

    const { storeInfo, deviceApi, currencyInfo } = useSelector(state => state);
    const { deviceGroup } = deviceApi?.data || {};
    const {
        nameEnabled,
        nameRequired,
        phoneNumberEnabled,
        phoneNumberRequired,
        emailAddressEnabled,
        emailAddressRequired,
    } = deviceGroup?.inputFields || defaultInputFields;
    const { iso2 } = currencyInfo || {};

    useEffect(() => {
        setDefaultCountry();
    }, []);

    const setDefaultCountry = () => {
        const countriesData = getPickerData();
        const defaultCountry = countriesData.find(country => country.iso2 === String(iso2).toLowerCase());
        setSelectedCountry(defaultCountry);
    };

    const onCountryChange = country => {
        const formattedPhoneNumber = FireboardUtils.inputFormattedPhnNum(state.phoneNumber, country.iso2);
        setSelectedCountry(country);
        setState({
            ...state,
            phoneNumber: formattedPhoneNumber,
        });
    };

    const onChangeInput = (key, value) => {
        if (key === 'note') {
            setState({
                ...state,
                [key]: document.getElementById("noteArea").value,
            })
        } else if (key === 'staffComment') {
            setState({
                ...state,
                [key]: document.getElementById("staffCommentArea").value,
            })
        } else {
            setState({
                ...state,
                [key]: value,
            })
        }
    };

    const onSubmit = () => {
        try {
            const { id: storeId } = storeInfo?.data || {};
            const payload = {
                countryCode: state.phoneNumber ? selectedCountry.dialCode.split('+')[1] : '',
                phoneNumber: state.phoneNumber ? FireboardUtils.inputUnFormattedPhnNum(state.phoneNumber, selectedCountry.iso2) : '',
                name: state.name.trim() || '',
                emailAddress: state.emailAddress.trim() || '',
                note: state.note.trim() || '',
                comment: state.staffComment.trim() || '',
                partySize: +state.partySize,
                businessDate: new Date().toISOString(),
                bookingDate: new Date().toISOString(),
                completeDate: null,
                status: 0,
                stationId: null,
                memberId: null,
                storeId,
                id: 0,
            };
            onCreateBooking && onCreateBooking(payload);
        } catch (error) {
            console.error(error);
        }
    };

    const isSubmitDisabled = () => {
        let isEmailValid = true, isNameValid = true, isPhoneNumberValid = true;
        if (emailAddressEnabled && (emailAddressRequired || (!emailAddressRequired && state.emailAddress))) {
            isEmailValid = EmailRegex.test(state.emailAddress.trim().toLowerCase());
        }
        if (nameEnabled && (nameRequired || (!nameRequired && state.name))) {
            isNameValid = ((state.name).trim()).length > 0;
        }
        if (phoneNumberEnabled && (phoneNumberRequired || (!phoneNumberRequired && state.phoneNumber))) {
            isPhoneNumberValid = (FireboardUtils.inputUnFormattedPhnNum(state.phoneNumber, selectedCountry.iso2)).length >= 10;
        }
        const isPartySizeValid = state.partySize.length > 0;
        return !(isEmailValid && isNameValid && isPhoneNumberValid && isPartySizeValid);
    };

    return (
        <Box height={'100vh'} overflow={'scroll'} className={classes.container}>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} className={classes.header}>
                <Typography variant='h6' className={classes.title}>{strings.CREATE_WAITLIST_CREATING_WAITLIST}</Typography>
                <IconButton className={classes.closeImg} onClick={goToWaitlistScreen}>
                    <HighlightOffIcon />
                </IconButton>
            </Box>
            <Box padding={2}>
                {nameEnabled && (
                    <TextInput
                        placeholder={`${strings.CREATE_WAITLIST_NAME} ${nameRequired ? '' : `(${strings.CREATE_WAITLIST_OPTIONAL})`}`}
                        value={state.name}
                        type='text'
                        onChange={value => onChangeInput('name', value)}
                        inputStyle={classes.input}
                        labelStyle={classes.label}
                    />
                )}
                <TextInput
                    placeholder={strings.CREATE_WAITLIST_PARTY_SIZE}
                    value={state.partySize}
                    type='number'
                    onChange={value => onChangeInput('partySize', value)}
                    inputStyle={classes.input}
                    labelStyle={classes.label}
                />
                {phoneNumberEnabled && (
                    <React.Fragment>
                        <Typography variant='body2' className={classes.label}>{`${strings.CREATE_WAITLIST_PHONE}${phoneNumberRequired ? '*' : ` (${strings.CREATE_WAITLIST_OPTIONAL})`}`}</Typography>
                        <Box display={'flex'} flexDirection={'row'} justifyContent={'space-around'} marginBottom={1.2}>
                            <Box width={'60%'}>
                                <CountryPicker
                                    selectedCountry={selectedCountry}
                                    onCountryChange={onCountryChange}
                                    containerStyle={cn(classes.input, classes.contryInput)}
                                />
                            </Box>
                            <Box width={'40%'}>
                                <input
                                    type='tel'
                                    value={FireboardUtils.inputFormattedPhnNum(state.phoneNumber, selectedCountry.iso2)}
                                    onChange={event => onChangeInput('phoneNumber', event.target.value)}
                                    className={classes.input}
                                />
                            </Box>
                        </Box>
                    </React.Fragment>
                )}
                {emailAddressEnabled && (
                    <TextInput
                        placeholder={`${strings.CREATE_WAITLIST_EMAIL}${emailAddressRequired ? '*' : ` (${strings.CREATE_WAITLIST_OPTIONAL})`}`}
                        value={state.emailAddress}
                        type='text'
                        onChange={value => onChangeInput('emailAddress', value)}
                        inputStyle={classes.input}
                        labelStyle={classes.label}
                    />
                )}
                <Box display={'flex'} flexDirection={'column'}>
                    <Typography variant='body2' className={classes.label}>{`${strings.CREATE_WAITLIST_NOTE} (${strings.CREATE_WAITLIST_OPTIONAL})`}</Typography>
                    <textarea
                        rows={3}
                        maxLength={100}
                        id="noteArea"
                        value={state.note}
                        onChange={value => onChangeInput('note', value)}
                        className={classes.textarea}
                    ></textarea>
                </Box>
                <Box display={'flex'} flexDirection={'column'}>
                    <Typography variant='body2' className={classes.label}>{`${strings.CREATE_WAITLIST_STAFF_COMMENT} (${strings.CREATE_WAITLIST_OPTIONAL})`}</Typography>
                    <textarea
                        rows={3}
                        maxLength={100}
                        id="staffCommentArea"
                        value={state.staffComment}
                        onChange={value => onChangeInput('staffComment', value)}
                        className={classes.textarea}
                    ></textarea>
                </Box>
            </Box>
            <Box display={'flex'} flexDirection={'column'} padding={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                    className={classes.button}
                    disabled={isSubmitDisabled()}
                >
                    {strings.CREATE_WAITLIST_ADD_TO_WAITLIST}
                </Button>
                <Button
                    variant="outlined"
                    color='primary'
                    onClick={goToWaitlistScreen}
                    className={classes.secondaryButton}
                >
                    {strings.CREATE_WAITLIST_CANCEL}
                </Button>
            </Box>
        </Box>
    );
};

export default CreateWaitList;

CreateWaitList.propTypes = {
    goToWaitlistScreen: PropTypes.func,
    onCreateBooking: PropTypes.func,
};

CreateWaitList.defaultProps = {
    goToWaitlistScreen: () => { },
    onCreateBooking: () => { },
};
